"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { futurIANavigation } from "@/lib/futur-ia-navigation";

export function BandoSmartSidebar({ activeHref, compact = false }: { activeHref: string; compact?: boolean }) {
  const [isCompact, setIsCompact] = useState(compact);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasProfileOpen = useRef(false);

  useEffect(() => {
    if (!isProfileOpen) return;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsProfileOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isProfileOpen]);

  useEffect(() => {
    if (isProfileOpen) {
      wasProfileOpen.current = true;
    } else if (wasProfileOpen.current) {
      profileButtonRef.current?.focus();
      wasProfileOpen.current = false;
    }
  }, [isProfileOpen]);

  const renderLink = ({ label, icon, href }: { label: string; icon: string; href: string }) => <Link
    className={`legal-nav-link ${activeHref === href ? "active" : ""}`}
    href={href}
    key={href}
    aria-label={label}
    aria-current={activeHref === href ? "page" : undefined}
    title={label}
  >
    <span className="legal-nav-icon" aria-hidden="true">{icon}</span>
    <span className="legal-nav-label">{label}</span>
  </Link>;

  return <aside className={`legal-sidebar ${isCompact ? "is-compact" : ""}`}>
    <Link className="legal-brand" href="/" aria-label="FuturIA, vai a Oggi">
      <span aria-hidden="true">FUTUR<span className="legal-brand-blue">IA</span></span>
    </Link>
    {compact && <button className="sidebar-collapse" type="button" onClick={() => setIsCompact((value) => !value)} aria-label={isCompact ? "Espandi navigazione" : "Comprimi navigazione"} title={isCompact ? "Espandi navigazione" : "Comprimi navigazione"}>{isCompact ? "→" : "←"}</button>}
    <nav aria-label="Navigazione principale" className="legal-nav">
      {renderLink({ label: "Oggi", icon: "✦", href: "/" })}
      {futurIANavigation.map((section) => <section className="legal-nav-group" key={section.group} aria-label={section.group}>
        <h2>{section.group}</h2>
        {section.links.map(renderLink)}
      </section>)}
    </nav>
    <div className="legal-sidebar-bottom">
      <Link href="/impostazioni" className={activeHref === "/impostazioni" ? "active" : ""} aria-current={activeHref === "/impostazioni" ? "page" : undefined} title="Impostazioni">
        <span className="legal-nav-icon" aria-hidden="true">⚙</span><span>Impostazioni</span>
      </Link>
      <button ref={profileButtonRef} className="legal-profile" type="button" onClick={() => setIsProfileOpen(true)} aria-label="Apri il profilo di Raffaele Battaglini, Amministratore" aria-controls="legal-profile-dialog" aria-expanded={isProfileOpen} title="Profilo di Raffaele Battaglini">
        <span className="avatar" aria-hidden="true">RB</span><span className="legal-profile-copy"><strong>Raffaele Battaglini</strong><small>Amministratore</small></span><span aria-hidden="true">⋯</span>
      </button>
    </div>
    {isProfileOpen && <div className="legal-profile-backdrop" onMouseDown={() => setIsProfileOpen(false)}><section id="legal-profile-dialog" className="legal-profile-dialog" role="dialog" aria-modal="true" aria-labelledby="legal-profile-title" onMouseDown={(event) => event.stopPropagation()}><button ref={closeButtonRef} className="legal-profile-close" type="button" onClick={() => setIsProfileOpen(false)} aria-label="Chiudi il profilo">×</button><div className="legal-profile-heading"><span className="legal-profile-avatar-large" aria-hidden="true">RB</span><div><p className="legal-profile-eyebrow">PROFILO UTENTE</p><h2 id="legal-profile-title">Raffaele Battaglini</h2><p>Amministratore</p></div></div><p className="legal-profile-demo-note">Profilo demo dell&apos;area di lavoro FuturIA.</p></section></div>}
    </aside>;
}
